import type { PaletteDef } from "@hyperlight/shared";
import { alpineSnow, groundFertility, groundForest } from '@hyperlight/shared';
export { groundFertility, groundForest, hasLivingGround } from '@hyperlight/shared';

/** Surface rules shared by the terrain shader and CPU vegetation placement.
 * Heights are metres above the planet's base radius, never amplitude units. */
export const GROUND = {
  beachStart: .8, beachEnd: 4.5,
  snowCold: -.65, snowWarm: -.35, snowRise: 60,
} as const;

const smooth = (a: number, b: number, x: number): number => {
  const k = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return k * k * (3 - 2 * k);
};

export function groundSnow(temp: number, heightM = 0, snowlineM = 1e9): number {
  return Math.max(1 - smooth(GROUND.snowCold, GROUND.snowWarm, temp),
    smooth(snowlineM, snowlineM + GROUND.snowRise, heightM));
}

export function groundBeach(heightAboveSeaM: number, slope = 1): number {
  return (1 - smooth(GROUND.beachStart, GROUND.beachEnd, heightAboveSeaM))
    * smooth(.85, .97, slope);
}

/** Sheltered sediment banks, not every beach. The same continuous field is
 * carried by terrain vertices and used by the depth-aware wetland scatter. */
export function groundMud(heightAboveSeaM: number, shelter: number, temp: number, slope = 1): number {
  return smooth(.12, .65, shelter) * smooth(-3, -1, heightAboveSeaM)
    * (1 - smooth(1.4, 4, heightAboveSeaM)) * smooth(-.55, -.25, temp) * smooth(.80, .96, slope);
}


/** Warm, seasonally dry living country. This climate field is shared by
 * canopy placement, near blades and terrain; it does not create a new biome. */
export function groundSavanna(temp: number, moist: number): number {
  return smooth(.20, .28, temp) * (1 - smooth(.68, .86, temp))
    * smooth(-.45, -.24, moist) * (1 - smooth(-.08, .12, moist));
}

/** Dry-country colour and grass begin above the coastal communities. */
export function groundSavannaInland(temp: number, moist: number, heightAboveSeaM: number): number {
  return groundSavanna(temp, moist) * smooth(8, 18, heightAboveSeaM);
}

export function groundMeadow(patch: number): number { return smooth(.22, .62, patch); }

/** Mobile sand is a shared physical habitat, not an archetype-wide recolour.
 * Sparse edge plants survive; interiors expose the broad dune planes. */
export function groundDuneSuppression(sand: number, kind: 'tree' | 'plant' | 'rock' = 'plant'): number {
  return kind === 'tree' ? smooth(.04, .58, sand) : kind === 'rock' ? smooth(.18, .86, sand) : smooth(.08, .80, sand);
}

/** Neutral warm linear pigment; sunrise/golden-hour colour comes from light. */
export const DUNE_SAND_ALBEDO = [.60, .51, .39] as const;
export const DUNE_SAND_BLEND = .65;
export const DUNE_DETAIL_GAIN = .28;

/** Mineral pigment only: the sun supplies the orange light in warm scenes.
 * Broad sediment beds use physical radial height, independent of tile/LOD. */
export const BUTTE_ALBEDO = [.38, .235, .165] as const;
export const BUTTE_ALBEDO_BLEND = .90;
export const BUTTE_DETAIL_GAIN = .38;
export function groundButteBand(heightM:number):number {
  const h=heightM/237,p=h-Math.floor(h);
  // Unequal bed depths and separations keep long planes quiet; no repeated
  // evenly spaced stripes or local detail noise competing with the silhouette.
  return 1-.06*smooth(.11,.118,p)*(1-smooth(.175,.19,p))
    -.04*smooth(.43,.447,p)*(1-smooth(.615,.635,p))
    +.02*smooth(.82,.833,p)*(1-smooth(.86,.874,p));
}

/** Iron-bearing sandstone, expressed as linear mineral pigment. Native light
 * supplies warm highlights and cool shaded planes; these are not lit colours. */
export const CANYON_OCHRE_ALBEDO = [.58, .245, .115] as const;
export const CANYON_PALE_ALBEDO = [.645, .380, .225] as const;
export const CANYON_IRON_ALBEDO = [.385, .145, .085] as const;
export const CANYON_FLOOR_ALBEDO = [.55, .325, .185] as const;
export const CANYON_FLOOR_BLEND = .84;
export const CANYON_DETAIL_GAIN = .72;
const canyonBed=(h:number,a:number,b:number)=>smooth(a-.55,a+.55,h)*(1-smooth(b-.55,b+.55,h));
/** Unequal mineral beds use existing world-fixed fields for broken edges and
 * plane pigmentation. There is no mesh-owned pattern or lighting baked in. */
export function groundCanyonAlbedo<T extends GroundColor>(heightM:number,out:T,mineral=.5,patch=.5):T {
  const height=heightM+(patch-.5)*2.8+(mineral-.5)*1.5,h=height-Math.floor(height/143)*143;
  const pale=(canyonBed(h,9,20)+canyonBed(h,58,74)+canyonBed(h,112,124))*.72;
  const iron=(canyonBed(h,28,31.5)+canyonBed(h,82,87)+canyonBed(h,133,136))*(.50+.35*mineral);
  const color=(i:number)=>((CANYON_OCHRE_ALBEDO[i]+(CANYON_PALE_ALBEDO[i]-CANYON_OCHRE_ALBEDO[i])*pale)*(1-iron)+CANYON_IRON_ALBEDO[i]*iron)*(.90+.20*mineral);
  out.r=color(0);out.g=color(1);out.b=color(2);return out;
}

/** Crystals and other alien flora do not imply organic soil or meadows. */

export interface GroundSite {
  heightM: number;
  seaLevelM: number;
  snowlineM: number;
  slope: number;
  snowRetention?: number;
  river: number;
  hasFlora: boolean;
  patch: number;
  estuaryShelter?: number;
  wetlandMask?: number;
  wetlandMud?: number;
  riverBankSediment?: number;
  riverBankRock?: number;
  duneSand?: number;
  butteRock?: number;
  /** Exposed canyon rock and the full sediment-bearing interior are separate. */
  canyonRock?: number;
  canyonGround?: number;
  /** Existing angular mineral texture channel; optional for substrate callers. */
  canyonMineral?: number;
}

export interface GroundColor { r: number; g: number; b: number }

/** Linear albedo, before fine grain and lighting. Grass inherits this exact
 * composition so its distance fade reveals the same habitat underneath it. */
export function groundColor<T extends GroundColor>(palette: PaletteDef, temp: number, moist: number, site: GroundSite, out: T): T {
  const tf = Math.min(1, Math.max(0, temp * .5 + .5)) * 2.999;
  const mf = Math.min(1, Math.max(0, moist * .5 + .5)) * 1.999;
  const tr = Math.floor(tf), mc = Math.floor(mf), tb = tf - tr, mb = mf - mc;
  const a = palette.table[tr * 3 + mc], b = palette.table[tr * 3 + mc + 1];
  const c = palette.table[(tr + 1) * 3 + mc], d = palette.table[(tr + 1) * 3 + mc + 1];
  const lerp = (x: number, y: number, k: number) => x + (y - x) * k;
  const water = smooth(.4, .75, site.river) * (site.heightM >= site.seaLevelM ? 1 : 0);
  const dune = Math.max(0, Math.min(1, site.duneSand ?? 0)) * (1 - water);
  const butte = Math.max(0, Math.min(1, Math.max(site.butteRock ?? 0,site.canyonRock ?? 0))) * (1 - water);
  const butteBand = butte>0 ? groundButteBand(site.heightM) : 1;
  const canyon = Math.max(0,Math.min(1,site.canyonRock??0))*(1-water);
  const canyonGround = Math.max(0,Math.min(1,site.canyonGround??0))*(1-water);
  const canyonColor = canyon>0 ? groundCanyonAlbedo(site.heightM,{r:0,g:0,b:0},site.canyonMineral??.5,site.patch) : null;
  const rock = (1 - smooth(.60, .88, site.slope)) * (1 - dune);
  const snow = alpineSnow(temp, site.heightM, site.snowlineM,site.slope,site.snowRetention??-1,site.patch)
    * (1 - water) * (site.heightM >= site.seaLevelM ? 1 : 0);
  const meadow = groundMeadow(site.patch);
  const fertility = groundFertility(temp, moist);
  const forest = groundForest(temp, moist) * (1 - .35 * meadow);
  const wet = smooth(.3, .8, moist) * (1 - meadow) * fertility;
  const savanna = groundSavannaInland(temp, moist, site.heightM - site.seaLevelM);
  const shade = [.68, .90, .70], soilTint = [.48, .39, .28], wetTint = [.79, .87, .77];
  const color = (i: number): number => {
    const biome = lerp(lerp(a[i], b[i], mb), lerp(c[i], d[i], mb), tb);
    let g = biome;
    if (site.hasFlora) {
      const soil = lerp(palette.sand[i] * soilTint[i], palette.cliff[i] * .62, .3);
      g = lerp(soil, biome, .42 + .58 * fertility * (.24 + .76 * meadow));
      const litter = lerp(soil, biome * shade[i], .18 + .25 * meadow);
      g = lerp(g, litter, forest * .88);
      g *= 1 + (wetTint[i] - 1) * wet * .35;
      // Neutral dry straw under the actual star light. Reuse the existing
      // meadow field for exposed soil gaps instead of another shader noise.
      const straw = lerp(palette.sand[i] * [.78, .79, .68][i], biome, .14);
      g = lerp(g, lerp(soil, straw, .50 + .50 * meadow), savanna);
    }
    if (site.seaLevelM > -1e8) {
      const depth = site.seaLevelM - site.heightM;
      const dk = smooth(0, 18, depth);
      g = lerp(g, palette.sand[i] * [.45, .48, .55][i], dk);
      g = lerp(g, palette.sand[i], groundBeach(-depth, site.slope) * (1 - dk));
      if (site.hasFlora) {
        const mud = lerp(palette.sand[i] * [.38, .34, .28][i], palette.cliff[i] * .50, .35)
          * lerp(.72, 1.10, smooth(-.1, 1.8, -depth));
        g = lerp(g, mud, groundMud(-depth, site.estuaryShelter ?? 0, temp, site.slope));
      }
    }
    if(site.hasFlora && (site.wetlandMask??0)>0) {
      const moss=lerp(biome*[.56,.65,.42][i],palette.cliff[i]*[.40,.48,.28][i],.35);
      const peat=lerp(palette.sand[i]*[.28,.25,.18][i],palette.cliff[i]*.32,.35);
      g=lerp(g,lerp(moss,peat,Math.min(1,Math.max(0,site.wetlandMud??0))),Math.min(1,site.wetlandMask!));
    }
    g = lerp(g, lerp(palette.sand[i], DUNE_SAND_ALBEDO[i], DUNE_SAND_BLEND), dune);
    g = lerp(g, palette.cliff[i], rock);
    if(site.hasFlora) {
      const sediment=Math.max(0,Math.min(1,site.riverBankSediment??0))*smooth(.72,.95,site.slope)*(1-water);
      const bankRock=Math.max(0,Math.min(1,site.riverBankRock??0))*(1-water);
      const mineral=lerp(palette.sand[i]*[.68,.64,.55][i],palette.cliff[i]*.8,.38)*(.92+.13*site.patch);
      g=lerp(g,mineral,sediment*.9);
      g=lerp(g,palette.cliff[i]*[.87,.92,.98][i]*(.94+.10*site.patch),bankRock*.9);
    }
    if(butte>0)g=lerp(g,lerp(palette.cliff[i],BUTTE_ALBEDO[i],BUTTE_ALBEDO_BLEND)*butteBand,butte);
    if(canyonGround>0)g=lerp(g,lerp(palette.sand[i],CANYON_FLOOR_ALBEDO[i],CANYON_FLOOR_BLEND),canyonGround);
    if(canyonColor)g=lerp(g,[canyonColor.r,canyonColor.g,canyonColor.b][i],canyon);
    return lerp(g, palette.snow[i], snow);
  };
  out.r = color(0); out.g = color(1); out.b = color(2);
  return out;
}

// Browser regression checks compare this GLSL with groundColor/groundSnow.
// Constants are emitted from the same metre-based definitions above.
const gl = (v: number) => Number.isInteger(v) ? `${v}.0` : String(v);
/** Bank pigment shares the existing broad habitat texture: no new samples. */
export const RIVER_BANK_SURFACE_GLSL = /* glsl */ `
vec3 groundRiverBank(vec3 ground, vec3 sand, vec3 cliff, vec2 bank, float slope, float habitatPatch, float water) {
  float sediment=clamp(bank.x,0.0,1.0)*smoothstep(.72,.95,slope)*(1.0-water);
  float rock=clamp(bank.y,0.0,1.0)*(1.0-water);
  vec3 mineral=mix(sand*vec3(.68,.64,.55),cliff*.8,.38)*(.92+.13*habitatPatch);
  ground=mix(ground,mineral,sediment*.9);
  return mix(ground,cliff*vec3(.87,.92,.98)*(.94+.10*habitatPatch),rock*.9);
}
`;
/** Injected only into tiles carrying exposed sandstone, not the base shader. */
export const BUTTE_SURFACE_GLSL = /* glsl */ `
vec3 groundButteAlbedo(vec3 cliff, float heightM) {
  float p = fract(heightM / 237.0);
  float band = 1.0 - .06 * smoothstep(.11, .118, p) * (1.0 - smoothstep(.175, .19, p))
    - .04 * smoothstep(.43, .447, p) * (1.0 - smoothstep(.615, .635, p))
    + .02 * smoothstep(.82, .833, p) * (1.0 - smoothstep(.86, .874, p));
  return mix(cliff, vec3(${BUTTE_ALBEDO.map(gl).join(', ')}), ${gl(BUTTE_ALBEDO_BLEND)}) * band;
}
`;
/** Compiled only on canyon tiles; no extra texture samples or dynamic noise. */
export const CANYON_SURFACE_GLSL = /* glsl */ `
float canyonBed(float h, float a, float b) {
  return smoothstep(a-.55,a+.55,h)*(1.0-smoothstep(b-.55,b+.55,h));
}
vec3 groundCanyonAlbedo(float heightM, float mineral, float habitatTone) {
  float height=heightM+(habitatTone-.5)*2.8+(mineral-.5)*1.5;
  float h=height-floor(height/143.0)*143.0;
  float pale=(canyonBed(h,9.0,20.0)+canyonBed(h,58.0,74.0)+canyonBed(h,112.0,124.0))*.72;
  float iron=(canyonBed(h,28.0,31.5)+canyonBed(h,82.0,87.0)+canyonBed(h,133.0,136.0))*(.50+.35*mineral);
  return mix(mix(vec3(${CANYON_OCHRE_ALBEDO.map(gl).join(', ')}),vec3(${CANYON_PALE_ALBEDO.map(gl).join(', ')}),pale),vec3(${CANYON_IRON_ALBEDO.map(gl).join(', ')}),iron)*(.90+.20*mineral);
}
vec3 groundCanyonSediment(vec3 sand) {
  return mix(sand,vec3(${CANYON_FLOOR_ALBEDO.map(gl).join(', ')}),${gl(CANYON_FLOOR_BLEND)});
}
`;
export const GROUND_SURFACE_GLSL = /* glsl */ `
vec3 groundDuneAlbedo(vec3 sand) {
  return mix(sand, vec3(${DUNE_SAND_ALBEDO.map(gl).join(', ')}), ${gl(DUNE_SAND_BLEND)});
}
float groundSnow(float temp, float heightM, float snowlineM) {
  return max(1.0 - smoothstep(${gl(GROUND.snowCold)}, ${gl(GROUND.snowWarm)}, temp),
    smoothstep(snowlineM, snowlineM + ${gl(GROUND.snowRise)}, heightM));
}
float alpineSnow(float temp,float h,float line,float slope,float retention,float habitatTone) {
  float p=groundSnow(temp,h,line);
  float cover=retention<0.0?p:smoothstep(0.0,.1,p)*smoothstep(.18,.34,p+(retention-.5)*.5+(habitatTone-.5)*.85);
  return cover*smoothstep(.72,.92,slope);
}
float groundBeach(float heightAboveSeaM, float slope) {
  return (1.0 - smoothstep(${gl(GROUND.beachStart)}, ${gl(GROUND.beachEnd)}, heightAboveSeaM)) * smoothstep(.85, .97, slope);
}
float groundMud(float heightAboveSeaM, float shelter, float temp, float slope) {
  return smoothstep(.12, .65, shelter) * smoothstep(-3.0, -1.0, heightAboveSeaM)
    * (1.0 - smoothstep(1.4, 4.0, heightAboveSeaM)) * smoothstep(-.55, -.25, temp) * smoothstep(.80, .96, slope);
}
float groundSavanna(float temp, float moist) {
  return smoothstep(.20, .28, temp) * (1.0 - smoothstep(.68, .86, temp))
    * smoothstep(-.45, -.24, moist) * (1.0 - smoothstep(-.08, .12, moist));
}
float groundSavannaInland(float temp, float moist, float heightAboveSeaM) {
  return groundSavanna(temp, moist) * smoothstep(8.0, 18.0, heightAboveSeaM);
}
vec3 groundHabitat(vec3 biome, vec3 sand, vec3 cliff, float temp, float moist, float habitatPatch, float heightAboveSeaM) {
  float meadow = smoothstep(.22, .62, habitatPatch);
  float fertility = smoothstep(-.55, -.25, temp) * smoothstep(-.4, -.1, moist);
  float forest = smoothstep(-.5, -.15, temp) * (1.0 - smoothstep(.45, .8, temp))
    * smoothstep(-.1, .4, moist) * (1.0 - .35 * meadow);
  vec3 soil = mix(sand * vec3(.48, .39, .28), cliff * .62, .3);
  vec3 ground = mix(soil, biome, .42 + .58 * fertility * (.24 + .76 * meadow));
  vec3 litter = mix(soil, biome * vec3(.68, .90, .70), .18 + .25 * meadow);
  ground = mix(ground, litter, forest * .88);
  float wet = smoothstep(.3, .8, moist) * (1.0 - meadow) * fertility;
  ground *= mix(vec3(1.0), vec3(.79, .87, .77), wet * .35);
  vec3 straw = mix(sand * vec3(.78, .79, .68), biome, .14);
  return mix(ground, mix(soil, straw, .50 + .50 * meadow), groundSavannaInland(temp, moist, heightAboveSeaM));
}
`;
